/**
 * Role context provider for tiered data filtering.
 *
 * Manages role selection within the current organization:
 * - Fetches user's assigned roles from /api/me/roles
 * - Persists selection in localStorage
 * - Invalidates React Query cache on role change
 * - Waits for OrgProvider to load before fetching roles
 *
 * Key features:
 * - "All Roles" view (null selectedRoleId) shows all accessible tiers
 * - Specific role selection filters role-tier data
 * - Cache invalidation on role switch prevents stale data
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useOrgContext } from "./OrgProvider";
import { api } from "../lib/api";

/**
 * Role information.
 */
export interface Role {
  id: string;
  name: string;
  description: string | null;
  priority: number;
}

/**
 * Role context state.
 */
/**
 * SYN-3115 — how the roles read went, as its own fact.
 *
 * Same vocabulary as OrgRolesStatus / WorkspaceRolesStatus elsewhere in this app, so there
 * is one way to say this rather than a new one per surface.
 *
 * `ready` is the ONLY status in which `roles` is a statement about the person. In
 * `unavailable` and `forbidden` it is an empty array because there is nothing to show —
 * never because they hold nothing. Anything that decides what a person can see or do must
 * check `status` before reading `roles.length`.
 */
export type RoleContextStatus = "loading" | "ready" | "forbidden" | "unavailable" | "busy";

/** SYN-3367: shown while the server is rate limiting us and we are waiting to ask again. */
export const ROLES_BUSY_MESSAGE = "Busy, retrying…";
/** Retry-After bounds for the busy state, and how many busy answers in a row we wait out. */
const BUSY_MIN_DELAY_MS = 1_000;
const BUSY_MAX_DELAY_MS = 60_000;
const BUSY_MAX_ATTEMPTS = 10;

export interface RoleContextState {
  /** Whether role data is still loading */
  isLoading: boolean;
  /** How the read went. `roles` is only a fact about the person when this is "ready". */
  status: RoleContextStatus;
  /** Buyer-facing explanation when status is "unavailable", "forbidden" or "busy". */
  error: string | null;
  /** Current selected role (null = "All Roles" view) */
  currentRole: Role | null;
  /** All roles the user is assigned to in current org */
  roles: Role[];
  /** Whether we're in "All Roles" view */
  isAllRolesView: boolean;
  /** Switch to a different role (null for "All Roles") */
  setCurrentRole: (roleId: string | null) => Promise<void>;
  /** Refresh roles list */
  refreshRoles: () => Promise<void>;
}

const STORAGE_KEY = "command.current-role";

/**
 * Save current role to localStorage.
 */
function persistRole(roleId: string | null): void {
  try {
    if (roleId) {
      localStorage.setItem(STORAGE_KEY, roleId);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Best effort
  }
}

/**
 * Load current role from localStorage.
 */
function loadPersistedRole(): string | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value && value !== "null" ? value : null;
  } catch {
    return null;
  }
}

/**
 * Safe default context for SSR and pre-hydration.
 */
const defaultContext: RoleContextState = {
  isLoading: true,
  status: "loading",
  error: null,
  currentRole: null,
  roles: [],
  isAllRolesView: true,
  setCurrentRole: async () => {},
  refreshRoles: async () => {},
};

const RoleContext = createContext<RoleContextState>(defaultContext);

interface RoleProviderProps {
  children: ReactNode;
}

/**
 * API response type for roles.
 */
interface RolesApiResponse {
  data: Role[];
  meta: { total: number };
}

/**
 * Role context provider.
 *
 * Must be nested inside OrgProvider - waits for org context before fetching.
 */
export function RoleProvider({ children }: RoleProviderProps) {
  const queryClient = useQueryClient();
  const { currentOrg, isLoading: orgLoading } = useOrgContext();

  const [roles, setRoles] = useState<Role[]>([]);
  const [currentRoleId, setCurrentRoleId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [status, setStatus] = useState<RoleContextStatus>("loading");
  const [error, setError] = useState<string | null>(null);
  const busyTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const busyAttempts = useRef(0);

  const clearBusyRetry = useCallback(() => {
    if (busyTimer.current !== undefined) {
      clearTimeout(busyTimer.current);
      busyTimer.current = undefined;
    }
  }, []);

  /**
   * Fetch roles for the current organization.
   */
  const fetchRoles = useCallback(async () => {
    // Wait for org to be set
    if (orgLoading || !currentOrg) {
      return;
    }
    clearBusyRetry();

    setIsLoading(true);
    setStatus("loading");
    try {
      const response = await api.get<RolesApiResponse>("/api/me/roles");
      const fetchedRoles = response.data;
      busyAttempts.current = 0;
      setRoles(fetchedRoles);
      setStatus("ready");
      setError(null);

      // Validate persisted role belongs to current org's roles
      const persistedRoleId = loadPersistedRole();
      let selectedRoleId: string | null = null;

      if (persistedRoleId && fetchedRoles.find((r) => r.id === persistedRoleId)) {
        selectedRoleId = persistedRoleId;
      }
      // Otherwise stay in "All Roles" view (null)

      setCurrentRoleId(selectedRoleId);
      persistRole(selectedRoleId);
    } catch (err) {
      // SYN-3115 — this used to be the last hop where a refusal became an absence. The
      // server answers 503 roles_authority_unavailable precisely so "we could not read
      // your roles" stays distinct from "you hold none"; emptying the list here threw that
      // away and the selector told the customer "No Roles".
      //
      // roles stays [] because there is genuinely nothing to show — but status says WHY,
      // and no surface may read roles.length as a fact about the person unless status is
      // "ready".
      console.error("[RoleProvider] Failed to fetch roles:", err);
      const httpStatus = (err as { status?: number } | null)?.status;
      setRoles([]);
      setCurrentRoleId(null);
      if (httpStatus === 429 && busyAttempts.current < BUSY_MAX_ATTEMPTS) {
        // SYN-3367 — the server is rate limiting us. That says nothing about the person's
        // roles and nothing about their session: it is "busy", and we ask again once the
        // server's Retry-After has passed (bounded), instead of "Roles unavailable".
        busyAttempts.current += 1;
        const retryAfter = (err as { retryAfterSeconds?: number } | null)?.retryAfterSeconds;
        const delayMs = Math.min(
          BUSY_MAX_DELAY_MS,
          Math.max(BUSY_MIN_DELAY_MS, (retryAfter ?? 5) * 1000),
        );
        setStatus("busy");
        setError(ROLES_BUSY_MESSAGE);
        busyTimer.current = setTimeout(() => {
          busyTimer.current = undefined;
          void fetchRolesRef.current();
        }, delayMs);
      } else if (httpStatus === 401 || httpStatus === 403) {
        setStatus("forbidden");
        setError("You do not have access to roles in this workspace.");
      } else {
        setStatus("unavailable");
        setError("We couldn't load your roles. Refresh and try again.");
      }
    } finally {
      setIsLoading(false);
    }
  }, [orgLoading, currentOrg, clearBusyRetry]);

  const fetchRolesRef = useRef(fetchRoles);
  fetchRolesRef.current = fetchRoles;

  // Fetch roles when org changes
  useEffect(() => {
    busyAttempts.current = 0;
    void fetchRoles();
    return clearBusyRetry;
  }, [fetchRoles, clearBusyRetry]);

  // Reset role when org changes to prevent stale selection
  useEffect(() => {
    // Do not validate against the initial empty role array. Waiting for the
    // fetch prevents a valid persisted selection from being cleared on mount.
    if (currentOrg && !isLoading) {
      // When org changes, validate that current role still exists
      const persistedRoleId = loadPersistedRole();
      if (persistedRoleId && !roles.find((r) => r.id === persistedRoleId)) {
        setCurrentRoleId(null);
        persistRole(null);
      }
    }
  }, [currentOrg?.orgId, isLoading, roles]);

  /**
   * Switch to a different role.
   *
   * Invalidates all React Query caches to ensure fresh data.
   */
  const setCurrentRole = useCallback(
    async (roleId: string | null) => {
      // Validate role exists (if not null)
      if (roleId && !roles.find((r) => r.id === roleId)) {
        console.warn(`[RoleProvider] Role not found: ${roleId}`);
        return;
      }

      // Update local state
      setCurrentRoleId(roleId);
      persistRole(roleId);

      // Invalidate all React Query caches (CRITICAL for tiered data consistency)
      await queryClient.invalidateQueries();
    },
    [roles, queryClient],
  );

  // Current role object
  const currentRole = useMemo(() => {
    if (!currentRoleId) {
      return null;
    }
    return roles.find((r) => r.id === currentRoleId) ?? null;
  }, [currentRoleId, roles]);

  const contextValue = useMemo<RoleContextState>(
    () => ({
      isLoading,
      status,
      error,
      currentRole,
      roles,
      isAllRolesView: currentRoleId === null,
      setCurrentRole,
      refreshRoles: fetchRoles,
    }),
    [isLoading, status, error, currentRole, roles, currentRoleId, setCurrentRole, fetchRoles],
  );

  return <RoleContext.Provider value={contextValue}>{children}</RoleContext.Provider>;
}

/**
 * Hook to access role context.
 */
export function useRoleContext(): RoleContextState {
  return useContext(RoleContext);
}

/**
 * Get the current role ID for API requests.
 * Returns null for "All Roles" view.
 */
export function useCurrentRoleId(): string | null {
  const { currentRole } = useRoleContext();
  return currentRole?.id ?? null;
}

/**
 * Check if we're in "All Roles" view.
 */
export function useIsAllRolesView(): boolean {
  const { isAllRolesView } = useRoleContext();
  return isAllRolesView;
}
